import {when} from 'mobx';
import {observer} from 'mobx-react';
import React, {useEffect, useState} from 'react';

import {CartCheckoutClickEvent, CheckoutReferrerHelper, COMPONENT_VERSION} from '@udemy/cart';
import {ClickEvent, Tracker} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {ButtonProps, Button} from '@udemy/react-core-components';
import {useFeatures, ExperimentImpressionEvent} from '@udemy/react-experimentation-provider';
import {serverOrClient} from '@udemy/shared-utils';
import {ShoppingClient} from '@udemy/shopping';
import {AuthenticatedUser, udLink, useUDData} from '@udemy/ud-data';

import {getConfig} from 'src/features/cart/config';

import {AuthAjaxModal} from '../auth/auth-ajax-modal.react-component';
import styles from './checkout-button.module.less';
import {attachCheckoutButtonParams} from './helpers';

type CheckoutButtonProps = {
    isDisabled?: boolean;
    authNextUrl?: string;
    className?: string;
    triggerMwebCartStickyClickEvent?: boolean;
    triggerMwebCartClickEvent?: boolean;
} & Pick<ButtonProps, 'size'>;

export const CheckoutButton: React.FC<CheckoutButtonProps> = observer(
    ({
        isDisabled = false,
        size = 'large',
        className,
        authNextUrl,
        triggerMwebCartClickEvent = false,
        triggerMwebCartStickyClickEvent = false,
    }) => {
        const {gettext, interpolate, ngettext} = useI18n();
        const {me: udMe} = useUDData();
        const config = getConfig(gettext, interpolate, ngettext);
        const [isLoading, setIsLoading] = useState(true);
        const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
        const hasAnyVouchers = ShoppingClient.lists.cart.items.some(
            (item) => item.buyable.buyable_object_type === 'certification_voucher',
        );
        const {featureVariantAssignments} = useFeatures(
            hasAnyVouchers ? [] : ['delayed_account_creation_on_checkout_page'],
        );
        const dacEnabled = hasAnyVouchers
            ? undefined
            : featureVariantAssignments.get('delayed_account_creation_on_checkout_page');

        useEffect(() => {
            (async () => {
                await when(() => !udMe.isLoading);
                setIsLoading(false);
            })();
        }, [udMe.isLoading]);
        const redirectsToCheckoutUrl = () => {
            ShoppingClient.freezeCheckoutList('cart');
            CheckoutReferrerHelper.saveCheckoutReferrer();
            if ((udMe as AuthenticatedUser).id) {
                serverOrClient.global.location.href = attachCheckoutButtonParams(
                    config.urls.checkoutPage,
                );
            } else if (!hasAnyVouchers && dacEnabled) {
                Tracker.publishEvent(
                    new ExperimentImpressionEvent(
                        dacEnabled.experimentIds,
                        dacEnabled.isLocalDefault,
                    ),
                );
                if (dacEnabled.configuration.dacOnCheckoutIsEnabled) {
                    serverOrClient.global.location.href = attachCheckoutButtonParams(
                        config.urls.checkoutPage,
                    );
                } else {
                    setIsAuthModalOpen(true);
                }
            } else {
                setIsAuthModalOpen(true);
            }
        };

        const handleCheckoutClick = () => {
            Tracker.publishEvent(
                new CartCheckoutClickEvent(
                    COMPONENT_VERSION.UDLITE as ConstructorParameters<
                        typeof CartCheckoutClickEvent
                    >[0],
                ),
            );
            if (triggerMwebCartStickyClickEvent) {
                Tracker.publishEvent(
                    new ClickEvent({
                        componentName: 'm_web_cart_sticky_checkout_button_action',
                    }),
                );
            } else if (triggerMwebCartClickEvent) {
                Tracker.publishEvent(
                    new ClickEvent({
                        componentName: 'm_web_cart_checkout_button_action',
                    }),
                );
            }
            redirectsToCheckoutUrl();
        };

        const hideAuthModal = () => {
            setIsAuthModalOpen(false);
        };

        const renderCheckoutButton = () => {
            const disabled = isLoading || isDisabled;
            return (
                <div>
                    <Button
                        udStyle="brand"
                        disabled={disabled}
                        onClick={handleCheckoutClick}
                        round={false}
                        size={size}
                        className={styles['checkout-button']}
                    >
                        <span>{gettext('Proceed to Checkout')}</span>
                        <Icon glyph="arrow-right" size="medium" label={false} />
                    </Button>
                    <div className={styles['checkout-button-terms']}>
                        {gettext("You won't be charged yet")}
                    </div>
                </div>
            );
        };

        return (
            <div className={className}>
                {renderCheckoutButton()}
                <AuthAjaxModal
                    isOpen={isAuthModalOpen}
                    onClose={hideAuthModal}
                    url={udLink.toAuth({
                        nextUrl: authNextUrl ?? config.urls.cartPage,
                    })}
                />
            </div>
        );
    },
);
